import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigationType } from 'react-router';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';

import { WaitingAlert, DangerAlert, Table, InfoAlert, SecondaryLink, BtnBar } from '@tmapy/style-guide';

import { createDownloadLink, useSelector } from '../../hooks';
import { QUERY_STATE } from '../../constants';
import { useSubjektyListResource } from '../../openapi/operations/subjektyList';
import { TableWithAsideLayout } from '../../app/components/TableWithAsideLayout';
import { FilterBtn } from '../../app/components/FilterBtn';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';

import { SubjectListItem } from './SubjectListItem';
import { SubjectFilter } from './SubjectFilter';
import { subjectMessages as msg, subjectBtnMessages } from './subjectMessages';

export const SubjectList: React.FC = () => {
  const dispatch = useDispatch();
  const navigationType = useNavigationType();
  const formatMessage = useMessage();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const filters = useSelector((state) => state.filter.subjectList);
  const { manager } = useSelector((state) => state.app.config);

  const query: Record<string, string | number> = {};
  for (const [key, val] of Object.entries(filters)) {
    if (typeof val !== 'undefined' && val !== '') {
      (query as any)[key] = val;
    }
  }

  const { queryState, data, fetchMore } = useSubjektyListResource(query, navigationType !== 'PUSH');

  const subjectList = data?.results ?? [];
  const subjectCount = data?.count;

  function handleNextSubjectsBtnClick() {
    if (data?.next) {
      fetchMore(data.next);
    }
  }

  function handleFilterBtnClick() {
    setFilterOpen(!isFilterOpen);
  }

  const isFiltered = Object.values(filters).some((filter) => filter);

  // CSV EXPORT
  const server = useSelector((state) => state.app.config.server);
  const queryFilterUrlString = useMemo(() => {
    const params = new URLSearchParams();

    for (const key in query) {
      if (Array.isArray(query[key])) {
        query[key].forEach((value) => params.append(key, value));
      } else {
        params.append(key, (query as any)[key]);
      }
    }

    return `&${params.toString()}`;
  }, [query]);

  const exportCsvLink = createDownloadLink(
    `${server}/dmx-server/subjekty/`,
    undefined,
    'download_csv',
    queryFilterUrlString,
  );

  return (
    <TableWithAsideLayout
      asideLabel={formatMessage(subjectBtnMessages.filter)}
      isAsideOpen={isFilterOpen}
      onAsideClose={handleFilterBtnClick}
    >
      <MainContent width='fit' scrollRestorationPrefix='subjectlist'>
        <article className='sg-u-vs-2'>
          <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
            <PageTitle>
              <FormattedMessage {...msg.subjectListPageTitle} />
            </PageTitle>
            <span className='sg-a-f-a' />

            <BtnBar>
              <FilterBtn isFiltered={isFiltered} onClick={handleFilterBtnClick} />

              {manager?.modules?.docExport && exportCsvLink && (
                <SecondaryLink size="sml" {...exportCsvLink} download>
                  <FormattedMessage {...subjectBtnMessages.exportCsv} />
                </SecondaryLink>
              )}
            </BtnBar>
          </header>
          {queryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...msg.subjectListStateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...msg.subjectListStateFail} />
            </DangerAlert>
          )}
          {subjectList.length > 0 ? (
            <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
              <div className='sg-a-mx--2'>
                <Table isClickable>
                  <thead>
                    <tr>
                      <th>
                        <FormattedMessage {...msg.name} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.person} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.ico} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.id} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.address} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.registry} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.note} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjectList.map((subjectItem) => (
                      <SubjectListItem key={subjectItem.id} {...subjectItem} />
                    ))}
                  </tbody>
                </Table>
              </div>
              <LoadNextItemsBar
                count={subjectList.length}
                total={subjectCount!}
                queryState={queryState}
                onNextBtnClick={handleNextSubjectsBtnClick}
              />
            </div>
          ) : (
            queryState === QUERY_STATE.SUCCESS && (
              <InfoAlert>
                <FormattedMessage {...msg.subjectListStateFilteredEmpty} />
              </InfoAlert>
            )
          )}
        </article>
      </MainContent>
      <SubjectFilter filters={filters} dispatch={dispatch} />
    </TableWithAsideLayout>
  );
};
